// The Activity tab's Prompt logs sub-tab (API-016, REQ-244, SCR-593), under either shell: the
// stored prompt bodies, metadata first, one body at a time in a row's summary. The shell owns the
// page header, the tabs and the sub-tab row, and decides whether the sub-tab is present
// (model.ts `promptLogsVisible`); this screen is what it shows once it is. It reads no URL: the
// shell hands it the query, whose `?event=…&ts=…` (a guardrail intervention's "Prompt" link)
// opens that event's summary on load, through the same audited single-event read.
//
// The API audits every list, read and body call, so the text filters apply on submit, not per
// keystroke, and the screen says so. Each row's person arrives named from the API (never a uuid
// as a label); the one page of users the screen loads feeds the user filter's picker only. A body
// lives only in the open summary's state and is dropped when it closes: never in a store, a URL,
// a download or the console.
import { useEffect, useId, useRef, useState } from "preact/hooks";
import { isAbort } from "../../api/client";
import { useApi } from "../../api/context";
import { ApiError } from "../../api/errors";
import type { OrgContext } from "../../api/org-context";
import { Field, hintId } from "../../primitives/field";
import { Chip, FilterBar } from "../../primitives/page";
import { RowDetail } from "../../primitives/row-detail";
import { ErrorBanner, Loading } from "../../primitives/states";
import { DataTable, type Column } from "../../primitives/table";
import { UNKNOWN_USER } from "../quotas/names";
import {
  AUDITED_NOTE,
  bodyFacts,
  canInflate,
  decodePromptBody,
  EMPTY_PROMPT_LOG_FILTERS,
  matchUsers,
  promptLogQuery,
  promptLogRow,
  promptLogTarget,
  renderTurns,
  USER_PAGE_LIMIT,
  userLabel,
  USERS_TRUNCATED_NOTE,
  usersTruncated,
  validatePromptLogFilters,
  type PromptBody,
  type PromptLogFilters,
  type PromptLogRow,
  type PromptLogTarget,
  type RenderedBlock,
  type User,
} from "./model";

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" });
}

export interface PromptLogsScreenProps {
  /** The org whose prompt logs are listed. */
  context: OrgContext;
  /** The URL's query, from the shell: `event` and `ts` open one event's summary. */
  query: URLSearchParams;
}

/** The audited note, the filters, the list and a row's summary, for a principal the shell let in. */
export function PromptLogsScreen({ context, query }: PromptLogsScreenProps) {
  return <PromptLogList orgId={context.orgId} target={promptLogTarget(query)} />;
}

function PromptLogList({ orgId, target }: { orgId: string; target: PromptLogTarget | null }) {
  const client = useApi();
  // `draft` is what the filter bar shows; `filters` what the list was last asked for. The text
  // fields reach `filters` on submit (every list call is audited); the dates at once.
  const [draft, setDraft] = useState<PromptLogFilters>(EMPTY_PROMPT_LOG_FILTERS);
  const [filters, setFilters] = useState<PromptLogFilters>(EMPTY_PROMPT_LOG_FILTERS);
  const [rows, setRows] = useState<PromptLogRow[]>([]);
  const [users, setUsers] = useState<readonly User[]>([]);
  const [usersCut, setUsersCut] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [selected, setSelected] = useState<PromptLogRow | null>(null);
  const [targetError, setTargetError] = useState<ApiError | null>(null);
  // The request in flight; a newer one aborts it, and a reply from an aborted one is ignored.
  const inFlight = useRef<AbortController | null>(null);

  const fieldErrors = validatePromptLogFilters(draft);
  const valid = Object.keys(fieldErrors).length === 0;

  const load = (cursor: string | null, append: boolean) => {
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    setLoading(true);
    setError(null);
    client
      .get("/v1/orgs/{orgId}/prompt-logs", { path: { orgId }, query: promptLogQuery(filters, cursor), signal: controller.signal })
      .then((page) => {
        if (controller.signal.aborted) return;
        const shaped = page.items.map(promptLogRow);
        setRows((current) => (append ? [...current, ...shaped] : shaped));
        setNextCursor(page.nextCursor ?? null);
      })
      .catch((err: unknown) => {
        if (!isAbort(err) && !controller.signal.aborted) setError(unexpected(err));
      })
      .finally(() => {
        if (inFlight.current === controller) {
          inFlight.current = null;
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    // One page of users feeds the user picker; a user beyond it cannot be picked (listUsers has
    // no search parameter yet), and the picker says so. The rows are named by the API itself.
    client
      .get("/v1/orgs/{orgId}/users", { path: { orgId }, query: { limit: USER_PAGE_LIMIT } })
      .then((page) => {
        setUsers(page.items);
        setUsersCut(usersTruncated(page));
      })
      .catch(() => undefined);
  }, [client, orgId]);

  // A filter change starts the list over: first page, no cursor from the previous filter.
  useEffect(() => {
    setRows([]);
    setNextCursor(null);
    load(null, false);
    return () => inFlight.current?.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, orgId, filters]);

  // A linked event opens its summary once, read (and audited) as one prompt log.
  const targetEvent = target?.eventId;
  const targetTs = target?.ts;
  useEffect(() => {
    if (!targetEvent || !targetTs) return;
    const controller = new AbortController();
    setTargetError(null);
    client
      .get("/v1/orgs/{orgId}/prompt-logs/{eventId}", { path: { orgId, eventId: targetEvent }, query: { ts: targetTs }, signal: controller.signal })
      .then((item) => {
        if (!controller.signal.aborted) setSelected(promptLogRow(item));
      })
      .catch((err: unknown) => {
        if (!isAbort(err) && !controller.signal.aborted) setTargetError(unexpected(err));
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, orgId, targetEvent, targetTs]);

  // The dates apply at once (the text fields wait for the form's submit). An invalid range lists
  // nothing until it is put right. The same `filters` object is kept when nothing changed, so
  // mounting does not list twice.
  useEffect(() => {
    if (valid) {
      setFilters((current) => (current.from === draft.from && current.to === draft.to ? current : { ...current, from: draft.from, to: draft.to }));
    } else {
      inFlight.current?.abort();
      setRows([]);
      setNextCursor(null);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.from, draft.to, valid]);

  const edit = (field: keyof PromptLogFilters) => (event: { currentTarget: { value: string } }) => {
    const value = event.currentTarget.value;
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const columns: Column<PromptLogRow>[] = [
    { key: "ts", header: "Time", cell: (row) => <span class="nowrap">{when(row.ts)}</span>, compare: (a, b) => a.ts.localeCompare(b.ts) },
    {
      key: "user",
      header: "User",
      cell: (row) => row.user,
      compare: (a, b) => a.user.localeCompare(b.user),
    },
    { key: "model", header: "Model", cell: (row) => <span class="mono">{row.model}</span>, compare: (a, b) => a.model.localeCompare(b.model) },
    { key: "messages", header: "Messages", numeric: true, cell: (row) => row.messageCount.toLocaleString("en-US"), compare: (a, b) => a.messageCount - b.messageCount },
    { key: "size", header: "Size", numeric: true, cell: (row) => <span title={`${row.bytesRaw.toLocaleString("en-US")} bytes`}>{row.size}</span>, compare: (a, b) => a.bytesRaw - b.bytesRaw },
    { key: "status", header: "Body", help: "promptLogs.bodyStatus", cell: (row) => <Chip tone={row.status.tone}>{row.status.label}</Chip> },
  ];

  return (
    <>
      <p class="banner banner-info" role="note">
        {AUDITED_NOTE}
      </p>
      {targetError && <ErrorBanner error={targetError} details={false} />}
      <form
        aria-label="Prompt log filters"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) setFilters(draft);
        }}
      >
        <FilterBar
          end={
            <button type="submit" class="button button-secondary" disabled={!valid}>
              Filter
            </button>
          }
        >
          <Field id="prompt-logs-user" label="User" help="promptLogs.user" compact error={fieldErrors.userId} hint={usersCut ? USERS_TRUNCATED_NOTE : undefined}>
            <UserPicker
              id="prompt-logs-user"
              users={users}
              value={draft.userId}
              onChange={(userId) => setDraft((current) => ({ ...current, userId }))}
              describedBy={usersCut ? hintId("prompt-logs-user") : undefined}
            />
          </Field>
          <Field id="prompt-logs-model" label="Model" help="promptLogs.model" compact error={fieldErrors.model}>
            <input
              id="prompt-logs-model"
              type="search"
              class="input-mono"
              autocomplete="off"
              spellcheck={false}
              placeholder="Bedrock model id"
              value={draft.model}
              onInput={edit("model")}
            />
          </Field>
          <Field id="prompt-logs-from" label="From" help="promptLogs.range" compact error={fieldErrors.from}>
            <input id="prompt-logs-from" type="date" value={draft.from} onInput={edit("from")} />
          </Field>
          <Field id="prompt-logs-to" label="To" help="promptLogs.range" compact error={fieldErrors.to}>
            <input id="prompt-logs-to" type="date" value={draft.to} onInput={edit("to")} />
          </Field>
        </FilterBar>
      </form>

      <div class="table-card">
        <DataTable
          caption="Prompt logs"
          hideCaption
          columns={columns}
          rows={rows}
          rowKey={(row) => row.eventId}
          loading={loading}
          error={error}
          onRetry={() => load(null, false)}
          emptyMessage={valid ? "No prompt logs match these filters." : "Put the dates right to list prompt logs."}
          nextCursor={nextCursor}
          onLoadMore={() => load(nextCursor, true)}
          onRowClick={setSelected}
          rowLabel={(row) => `Prompt log ${when(row.ts)}, ${row.user}`}
        />
      </div>

      {selected && <PromptLogDetail key={selected.eventId} orgId={orgId} row={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

interface UserPickerProps {
  id: string;
  users: readonly User[];
  /** The picked user's id, or "" for every user. */
  value: string;
  onChange(userId: string): void;
  describedBy?: string | undefined;
}

/**
 * The user filter, a combobox like the customer picker: type part of a name, email or external id
 * and pick a match, shown as name (email) and never by its external id (IA-1); the pick is the
 * user's id. Arrow keys
 * move, Enter picks, Esc closes; emptying the box, or "All users", clears the filter.
 */
function UserPicker({ id, users, value, onChange, describedBy }: UserPickerProps) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const root = useRef<HTMLDivElement>(null);

  const picked = users.find((user) => user.id === value);
  // A picked id the loaded page no longer holds is named "Unknown user", never shown as the id.
  const selectedLabel = value === "" ? "" : picked ? userLabel(picked) : UNKNOWN_USER;
  const typed = query.trim();
  const options: { id: string; name: string }[] = [
    ...(typed === "" ? [{ id: "", name: "All users" }] : []),
    ...matchUsers(users, typed).map((user) => ({ id: user.id, name: userLabel(user) })),
  ];

  useEffect(() => {
    if (!open) return;
    const onDown = (event: PointerEvent) => {
      if (root.current && event.target instanceof Node && !root.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const choose = (option: { id: string } | undefined) => {
    if (!option) return;
    onChange(option.id);
    setQuery("");
    setOpen(false);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Tab") {
      setOpen(false);
      setQuery("");
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(options.length - 1, i + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (event.key === "Enter") {
      // Picks rather than submitting the form; with the list closed, Enter submits as usual.
      if (open) {
        event.preventDefault();
        choose(options[active]);
      }
    } else if (event.key === "Escape") {
      if (open) {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        setQuery("");
      }
    }
  };

  const activeOption = open ? options[active] : undefined;
  return (
    <div class="combobox" ref={root}>
      <input
        id={id}
        class="input combobox-input"
        type="text"
        role="combobox"
        autocomplete="off"
        spellcheck={false}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeOption ? `${listId}-${active}` : undefined}
        aria-describedby={describedBy}
        placeholder="All users — search by name or email"
        value={open ? query : selectedLabel}
        onFocus={(event) => {
          setQuery("");
          setActive(0);
          setOpen(true);
          event.currentTarget.select();
        }}
        onInput={(event) => {
          const text = event.currentTarget.value;
          setQuery(text);
          setActive(0);
          setOpen(true);
          if (text.trim() === "") onChange("");
        }}
        onKeyDown={onKeyDown}
      />
      {open && (
        <ul class="combobox-list" id={listId} role="listbox" aria-label="Users">
          {options.length === 0 ? (
            <li class="combobox-empty" role="option" aria-disabled="true" aria-selected="false">
              No user matches.
            </li>
          ) : (
            options.map((option, index) => (
              <li
                key={`${option.id}-${index}`}
                id={`${listId}-${index}`}
                class="combobox-option"
                role="option"
                aria-selected={index === active}
                onPointerDown={(event) => {
                  event.preventDefault();
                  choose(option);
                }}
                onPointerEnter={() => setActive(index)}
              >
                <span class="combobox-option-name">{option.name}</span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

type BodyState = { kind: "idle" } | { kind: "loading" } | { kind: "loaded"; body: PromptBody } | { kind: "failed"; error: ApiError };

/** One prompt log's summary: its facts, and the body on request. Unmounting drops the body with
 *  the state and abandons a fetch still under way. */
function PromptLogDetail({ orgId, row, onClose }: { orgId: string; row: PromptLogRow; onClose(): void }) {
  const client = useApi();
  const [body, setBody] = useState<BodyState>({ kind: "idle" });
  const fetching = useRef<AbortController | null>(null);
  const inflates = canInflate();

  useEffect(() => () => fetching.current?.abort(), []);

  // A refused request shows the API's error; a stored object that will not inflate or parse is
  // "unreadable". Through the client, so a 401 ends the session as everywhere else.
  const loadBody = () => {
    const controller = new AbortController();
    fetching.current = controller;
    setBody({ kind: "loading" });
    client
      .getBytes("/v1/orgs/{orgId}/prompt-logs/{eventId}/body", { path: { orgId, eventId: row.eventId }, query: { ts: row.ts }, signal: controller.signal })
      .then((compressed) =>
        decodePromptBody(compressed).catch(() => {
          throw unreadable();
        }),
      )
      .then((decoded) => {
        if (!controller.signal.aborted) setBody({ kind: "loaded", body: decoded });
      })
      .catch((err: unknown) => {
        if (!isAbort(err) && !controller.signal.aborted) setBody({ kind: "failed", error: unexpected(err) });
      });
  };

  const loadAction =
    row.readable && body.kind === "idle" && inflates ? (
      <button type="button" class="button button-primary" onClick={loadBody}>
        Load body
      </button>
    ) : undefined;

  return (
    <RowDetail
      title={`Prompt log — ${when(row.ts)}`}
      open
      onClose={onClose}
      class="dialog-wide"
      facts={[
        {
          label: "User",
          value: row.user,
        },
        { label: "Model", value: <span class="mono">{row.model}</span> },
        { label: "Messages", value: row.messageCount.toLocaleString("en-US") },
        { label: "Size", value: row.size },
        { label: "Body", value: <Chip tone={row.status.tone}>{row.status.label}</Chip> },
      ]}
      actions={loadAction}
    >
      <div class="stack-sm">
        <p class="card-note">{AUDITED_NOTE}</p>
        {row.readable && !inflates && <p class="card-note">This browser cannot show prompt bodies.</p>}
        {body.kind === "loading" && <Loading label="Loading the prompt body…" rows={2} />}
        {body.kind === "failed" && <ErrorBanner error={body.error} onRetry={loadBody} />}
        {body.kind === "loaded" && <PromptBodyView body={body.body} />}
      </div>
    </RowDetail>
  );
}

function unreadable(): ApiError {
  return new ApiError(0, "body_unreadable", "The stored body is not a readable prompt document.");
}

function PromptBodyView({ body }: { body: PromptBody }) {
  const facts = bodyFacts(body);
  return (
    <>
      {facts.length > 0 && <p class="card-note">{facts.join(" · ")}</p>}
      <div class="prompt-body" tabindex={0} role="region" aria-label="Prompt body">
        {renderTurns(body).map((turn) => (
          <section key={turn.key} class="prompt-turn" aria-label={turn.label}>
            <h3 class="prompt-turn-label">{turn.label}</h3>
            {turn.blocks.map((block, index) => (
              <PromptBlock key={index} block={block} />
            ))}
          </section>
        ))}
      </div>
    </>
  );
}

function PromptBlock({ block }: { block: RenderedBlock }) {
  switch (block.kind) {
    case "text":
      return <pre class="prompt-text">{block.text}</pre>;
    case "tool_use":
      return (
        <div class="prompt-tool">
          <span class="prompt-tool-label">{block.label}</span>
          <pre class="prompt-text">{block.json}</pre>
        </div>
      );
    case "tool_result":
      return (
        <div class={block.isError ? "prompt-tool prompt-tool-error" : "prompt-tool"}>
          <span class="prompt-tool-label">
            {block.label}
            {block.isError && (
              <>
                {" "}
                <Chip tone="red">error</Chip>
              </>
            )}
          </span>
          <pre class="prompt-text">{block.text}</pre>
        </div>
      );
    case "opaque":
      return <span class="prompt-opaque muted">{block.label}</span>;
  }
}
